// This file has been created (totally or partially) with the assistance of artificial intelligence tools.
// All content has been generated under the direct supervision of a named individual,
// and under the AI.Backbone Orchestrator Compliance framework

/**
 * @file
 * Read More behavior.
 *
 * @description
 * JS-measured max-height accordion. -webkit-line-clamp/-webkit-box was
 * dropped because mobile WebKit does not reliably reset that layout on
 * expand, causing overlapping text. The collapsed height is snapped to the
 * real rendered line boundary instead of an approximated lineHeight * n
 * calculation, so the cut never lands mid-line. Lines are derived from the
 * text nodes' own client rects (grouped by vertical center) rather than a
 * single range over the whole content, so element/paragraph boxes never
 * inflate the line count.
 */

((Drupal, once) => {
  'use strict';

  // Number of lines to show while collapsed.
  const LINES_TO_SHOW = 6;

  // Sub-pixel/rendering tolerance when deciding whether two text fragments
  // sit on the same visual line (mixed inline fonts, links, bold, etc.).
  // Compared against each candidate line's fixed vertical center, not its
  // (growing) top/bottom, so it can never bridge into an adjacent line.
  const LINE_CENTER_TOLERANCE = 2;

  interface LineBounds {
    top: number;
    bottom: number;
    center: number;
  }

  /**
   * Returns the non-empty text nodes inside `root`, in document order.
   */
  function getTextNodes(root: HTMLElement): Text[] {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        return node.textContent?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    const nodes: Text[] = [];
    let node = walker.nextNode();
    while (node) {
      nodes.push(node as Text);
      node = walker.nextNode();
    }
    return nodes;
  }

  /**
   * Returns the client rects covering a single text node's rendered content.
   */
  function getTextRects(textNode: Text): DOMRect[] {
    const range = document.createRange();
    range.selectNodeContents(textNode);
    return Array.from(range.getClientRects());
  }

  /**
   * Merges rects that sit on the same visual line, sorted top to bottom.
   * Each rect is matched against a line's fixed center (the center of the
   * first rect that opened it), never against the line's merged top/bottom,
   * so two rects that merely touch at a line boundary (one's top equal to
   * the previous one's bottom) are never merged into a single line.
   */
  function groupIntoLines(rects: DOMRect[]): LineBounds[] {
    const lines: LineBounds[] = [];

    [...rects]
      .sort((a, b) => a.top - b.top)
      .forEach((rect) => {
        const center = rect.top + rect.height / 2;
        const line = lines.find((candidate) => Math.abs(center - candidate.center) < LINE_CENTER_TOLERANCE);
        if (line) {
          line.top = Math.min(line.top, rect.top);
          line.bottom = Math.max(line.bottom, rect.bottom);
        } else {
          lines.push({ top: rect.top, bottom: rect.bottom, center });
        }
      });

    return lines.sort((a, b) => a.top - b.top);
  }

  /**
   * Finds the pixel height of the first `lines` visual lines of content,
   * snapping exactly to the line boundary instead of estimating from
   * line-height (which can land mid-line depending on font/viewport).
   */
  function getClampedHeight(content: HTMLElement, lines: number): number {
    const fragments = getTextNodes(content)
      .flatMap((textNode) => getTextRects(textNode))
      .filter((rect) => rect.width > 0 && rect.height > 0);

    const visualLines = groupIntoLines(fragments);
    const targetLine = visualLines[lines - 1];

    if (!targetLine) {
      return content.scrollHeight;
    }

    return Math.ceil(targetLine.bottom - content.getBoundingClientRect().top);
  }

  /**
   * Initializes read-more expand/collapse behaviour.
   */
  Drupal.behaviors.tpReadMore = {
    attach(context: Document | HTMLElement) {
      const components = once('tp-read-more', '.read-more', context);

      components.forEach((component) => {
        const container = component as HTMLElement;
        const content = container.querySelector<HTMLElement>('.read-more__content');
        const toggle = container.querySelector<HTMLElement>('.read-more__toggle');
        const button = container.querySelector<HTMLButtonElement>('.read-more__button');

        if (!content || !toggle || !button) {
          return;
        }

        let collapsedHeight = getClampedHeight(content, LINES_TO_SHOW);

        // Hide the toggle entirely if the content fits within the collapsed height.
        if (content.scrollHeight <= collapsedHeight) {
          toggle.hidden = true;
          return;
        }

        content.style.maxHeight = `${collapsedHeight}px`;

        // Wire up ARIA attributes for the initial collapsed state.
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', content.id);

        const labelEl = button.querySelector<HTMLElement>('.button__label');
        const collapsedLabel = container.dataset.readMoreCollapsedLabel || Drupal.t('Read more');
        const expandedLabel = container.dataset.readMoreExpandedLabel || Drupal.t('Read less');

        button.addEventListener('click', () => {
          const isExpanded = container.classList.contains('read-more--expanded');

          if (isExpanded) {
            // Collapse. If a previous expand already released max-height to
            // "none" (see transitionend handler below), pin it back to the
            // current rendered height first: a transition cannot animate
            // away from "none", so collapsing straight from "none" would
            // otherwise snap shut instead of transitioning.
            if (content.style.maxHeight === 'none') {
              content.style.maxHeight = `${content.scrollHeight}px`;
              content.getBoundingClientRect();
            }
            content.style.maxHeight = `${collapsedHeight}px`;
            container.classList.remove('read-more--expanded');
            button.setAttribute('aria-expanded', 'false');
            if (labelEl) {
              labelEl.textContent = collapsedLabel;
            }
          } else {
            // Expand - measure the natural height at click time.
            content.style.maxHeight = `${content.scrollHeight}px`;
            container.classList.add('read-more--expanded');
            button.setAttribute('aria-expanded', 'true');
            if (labelEl) {
              labelEl.textContent = expandedLabel;
            }
          }
        });

        // Once fully expanded, release the pixel cap so content that grows
        // afterwards (lazy-loaded images/media, later reflow) is never
        // clipped behind overflow: hidden.
        content.addEventListener('transitionend', (event) => {
          if (event.propertyName === 'max-height' && container.classList.contains('read-more--expanded')) {
            content.style.maxHeight = 'none';
          }
        });

        // Recompute both heights on resize/orientation change so the clamp
        // stays snapped to a real line boundary at the new viewport width.
        window.addEventListener('resize', () => {
          collapsedHeight = getClampedHeight(content, LINES_TO_SHOW);
          const isExpanded = container.classList.contains('read-more--expanded');
          content.style.maxHeight = isExpanded ? 'none' : `${collapsedHeight}px`;
        });

        // A web font finishing load after initialization can reflow text
        // without firing a resize event, so re-snap to the real line boundary.
        document.fonts?.ready.then(() => {
          collapsedHeight = getClampedHeight(content, LINES_TO_SHOW);
          const isExpanded = container.classList.contains('read-more--expanded');
          content.style.maxHeight = isExpanded ? 'none' : `${collapsedHeight}px`;
        });
      });
    },
  };
})(Drupal, once);
